import React, { useEffect, useMemo, useState } from 'react';
import { useAnalysisHistory } from 'hooks/useAnalysisHistory';
import { useBacktestList, useRuleSet, ruleKey, sameRules, toRuleSet, type BacktestRuleSet } from 'hooks/useBacktest';
import { useBacktestSettings } from 'hooks/useBacktestSettings';
import { ENGINE_OPTIONS as ENGINE_DEFS, canUseEngine, engineLabel, engineVersionLabel, type Engine } from 'api/analysisApi';
import { useAuthStore } from 'store/useAuthStore';
import { ApiError } from 'api/restClient';
import { exportAnalyses, getAnalysis } from 'api/historyApi';
import { listUsers, type AdminUser } from 'api/adminApi';
import { downloadBlob, downloadZip } from 'utils/download';
import { analysisNo, buildTraceBundle } from 'utils/traceBundle';
import type { AnalysisAuditSummary, AnalysisHistoryFilters, BacktestAggregate, BacktestCombo, BacktestGrade, BacktestGradeStats, BacktestSettingsPatch, BacktestVerdict, ForkMode, HistorySortKey } from 'types/history';
import { HistoryDetailModal } from './HistoryDetailModal';
import { ForkSweepBar } from './ForkSweepBar';
import { BacktestSettingsModal } from './BacktestSettingsModal';
import { MultiSelectFilter } from './MultiSelectFilter';
import { fmtDateTime } from './format';
import { GRADE_ORDER, fmtPct, fmtR, gradeLabel, gradeShortLabel, gradeTone, outcomesByGrade, verdictLabel, verdictTone } from './backtestFormat';
import type { BacktestRollup } from 'types/history';
import './historyView.css';
import './backtest.css';
import './fork.css';

/**
 * Table columns, in render order.
 *
 * `sortKey` is the server's field name — sorting is server-side, so these are
 * the API's whitelist, not accessors into the loaded page. Columns without one
 * (Trend, Side, and the four grade columns) are derived rather than stored, so
 * the server cannot order by them and their headers are inert.
 */
type GradeColumnKey = `grade:${BacktestGrade}`;
type ColumnKey = HistorySortKey | 'trend' | 'side' | GradeColumnKey;

/** The grades that get their own column, left to right. */
const GRADE_COLUMNS: BacktestGrade[] = ['best', 'decent', 'risk', 'high_risk'];

const gradeKey = (g: BacktestGrade): GradeColumnKey => `grade:${g}`;

const COLUMNS: { key: ColumnKey; label: string; sortKey?: HistorySortKey; grade?: BacktestGrade }[] = [
	{ key: 'seq', label: '#', sortKey: 'seq' },
	{ key: 'created_at', label: 'Date / time', sortKey: 'created_at' },
	{ key: 'username', label: 'User', sortKey: 'username' },
	{ key: 'asset', label: 'Symbol', sortKey: 'asset' },
	{ key: 'trend', label: 'Trend' },
	{ key: 'side', label: 'Side' },
	{ key: 'timeframe', label: 'TF', sortKey: 'timeframe' },
	{ key: 'exchange', label: 'Exchange', sortKey: 'exchange' },
	{ key: 'engine', label: 'Engine', sortKey: 'engine' },
	// { key: 'status', label: 'Status', sortKey: 'status' },
	// { key: 'total_tokens', label: 'Tokens', sortKey: 'total_tokens' },
	// One column per trade grade, replacing the single Verdict column: the engine
	// ranks its own calls Best → High Risk, so putting the outcome under the grade
	// that produced it turns "did our Best calls actually win?" into reading down
	// a column. Same axes as the summary bar's breakdown, one row per analysis.
	...GRADE_COLUMNS.map((g) => ({ key: gradeKey(g), label: gradeShortLabel(g), grade: g })),
];

const SORTABLE_COLUMNS = COLUMNS.filter((c) => c.sortKey);

// Derived from the shared registry rather than re-listed here: a hand-kept copy
// drifted once already (it carried 'claude', which the registry lacked a label for).
const ENGINE_OPTIONS: Engine[] = ENGINE_DEFS.map((e) => e.value);
const EXCHANGE_OPTIONS = ['binance', 'mexc'];
const MARKET_OPTIONS = ['spot', 'futures'];
// Suggestions only — the audit log stores whatever timeframe was requested (45m,
// 8h…), so this is a free-text field with hints rather than a closed list.
const TIMEFRAME_SUGGESTIONS = ['5m', '15m', '30m', '45m', '1H', '4H', '8H', '12H', '1D', '3D', 'W'];

// Verdicts worth filtering by, most conclusive first. DATA_UNAVAILABLE is left
// out: it means the replay could not run, not an outcome to study.
const VERDICT_FILTER_OPTIONS: BacktestVerdict[] = ['TP_HIT', 'SL_HIT', 'TIMEOUT', 'OPEN', 'NO_ENTRY', 'PENDING_INSUFFICIENT_DATA', 'WAIT_SKIPPED', 'INVALID_PROPOSAL'];

// ─── Grade × outcome breakdown (summary bar) ─────────────────────────────────
//
// Same two axes as the table's grade columns above — grades across, outcomes
// down — so the bar totals and the rows beneath it read as one picture.

/** Not trade grades, so they earn a column only when they hold something. */
const GRADE_EXTRA_COLUMNS: BacktestGrade[] = ['wait', 'unknown'];

interface GradeRow {
	key: 'tp' | 'sl' | 'no_entry' | 'pending' | 'open' | 'timeout' | 'wait' | 'invalid';
	label: string;
	/** Spelled out on hover — the table is abbreviated to stay narrow. */
	title: string;
	tone?: 'good' | 'bad';
	/** Kept even at zero, so the table has a stable shape to scan. */
	always?: boolean;
}

/**
 * Every bucket a proposal can land in, so a column's rows always sum to the
 * total in its header — no outcome can hide in the gap. The four the operator
 * reads daily are pinned; the rest appear only when they hold something, since
 * a permanent row of zeros is noise while a missing one would be a lie.
 */
const GRADE_ROWS: GradeRow[] = [
	{ key: 'tp', label: 'TP', title: 'Take profit — a target was hit', tone: 'good', always: true },
	{ key: 'sl', label: 'SL', title: 'Stop loss — the stop was hit', tone: 'bad', always: true },
	{ key: 'no_entry', label: 'NE', title: 'No entry — price never reached the entry zone within the window', always: true },
	{ key: 'pending', label: 'P', title: 'Pending — not enough candles have printed yet to judge', always: true },
	{ key: 'open', label: 'O', title: 'Open — filled and still running at the end of the observation window' },
	{ key: 'timeout', label: 'TO', title: 'Timed out — closed by the time stop at neither target nor stop' },
	{ key: 'wait', label: 'W', title: 'Wait — the engine called the setup untradeable, so nothing was risked' },
	{ key: 'invalid', label: 'INV', title: 'Invalid — unusable levels (wrong-side target, zero-distance stop)' },
];

/**
 * <input type="date"> value ⇄ the ISO string the API wants.
 *
 * Day granularity rather than datetime-local: it fits a table header cell, and
 * a day is the unit people actually filter an audit log by. `until` therefore
 * has to land on the *end* of the chosen day — the API's range is inclusive, so
 * a bare midnight would silently exclude everything that ran that day.
 */
const toDateInput = (iso?: string): string => (iso ? iso.slice(0, 10) : '');
const fromDateInput = (v: string, edge: 'start' | 'end'): string | undefined => {
	if (!v) return undefined;
	const d = new Date(v + (edge === 'start' ? 'T00:00:00' : 'T23:59:59.999'));
	return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

export function HistoryView() {
	const settings = useBacktestSettings();
	// The pair sits above both queries: history needs it to scope a verdict
	// filter, and the backtest surface needs the rows history returns.
	const [pair, setPair] = useRuleSet(settings.settings);
	const { rows, total, status, error, filters, setFilters, refresh, offset, setOffset, pageSize, sortKey, sortDir, sortBy } = useAnalysisHistory(pair);
	const bt = useBacktestList(
		filters,
		rows.map((r) => r.id),
		pair,
	);
	const [selected, setSelected] = useState<AnalysisAuditSummary | null>(null);
	const [settingsOpen, setSettingsOpen] = useState(false);

	const canPrev = offset > 0;
	const canNext = offset + rows.length < total;
	const goPrev = () => canPrev && setOffset(Math.max(0, offset - pageSize));
	const goNext = () => canNext && setOffset(offset + pageSize);

	const patch = (next: Partial<AnalysisHistoryFilters>) => setFilters({ ...filters, ...next });
	// v4+ engines are confidential: not even offered as a filter without the flag
	// (the backend already omits their runs from every History response).
	const authUser = useAuthStore((s) => s.user);
	const engineFilterOptions = ENGINE_OPTIONS.filter((e) => canUseEngine(authUser, e));
	/** Selects send '' for "any"; the API wants the key absent, not empty. */
	const orUndefined = (v: string) => v || undefined;

	// Local text inputs committed on Enter / blur, so a filter change doesn't
	// re-query on every keystroke.
	const [assetText, setAssetText] = useState(filters.asset ?? '');
	const [tfText, setTfText] = useState(filters.timeframe ?? '');
	useEffect(() => setAssetText(filters.asset ?? ''), [filters.asset]);
	useEffect(() => setTfText(filters.timeframe ?? ''), [filters.timeframe]);

	const commitAsset = () => patch({ asset: orUndefined(assetText.trim().toUpperCase()) });
	const commitTf = () => patch({ timeframe: orUndefined(tfText.trim()) });

	// User picker options. Best-effort: a non-superuser (or a failure) simply
	// leaves the filter empty rather than breaking the toolbar.
	const [users, setUsers] = useState<AdminUser[]>([]);
	useEffect(() => {
		const ctrl = new AbortController();
		listUsers(0, 200, ctrl.signal)
			.then((u) => setUsers(u))
			.catch(() => undefined);
		return () => ctrl.abort();
	}, []);

	// An empty array is truthy, so a multi-valued filter has to be counted by its
	// length or "no engines selected" would read as an active filter.
	const activeFilterCount = useMemo(() => Object.values(filters).filter((v) => (Array.isArray(v) ? v.length > 0 : Boolean(v))).length, [filters]);

	/**
	 * Save the rules, then look at them.
	 *
	 * Without this the two controls silently disagree — settings could say entry
	 * 7 while the table still showed the entry-6 run — and nothing on screen said
	 * so. Saving now always moves the view to the rules you just set; if no sweep
	 * exists for them yet the bar says "not run", which is the honest state.
	 */
	const saveSettingsAndView = async (patch: BacktestSettingsPatch) => {
		const saved = await settings.save(patch);
		if (saved) setPair(toRuleSet({ ...saved, window: saved.entry_window }));
		return saved;
	};

	const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
	const clearFilters = () => {
		setAssetText('');
		setTfText('');
		setFilters({});
	};

	/**
	 * One filter control per column, keyed by the column it belongs under.
	 *
	 * Built once and rendered twice — in the table's filter row on desktop, and
	 * in the mobile panel where that row is hidden. Both are driven by the same
	 * state, so there is nothing to keep in sync.
	 */
	const filterControls: Partial<Record<ColumnKey, React.ReactNode>> = {
		created_at: (
			<div className='hist-filter-range'>
				<input className='hist-filter-input' type='date' value={toDateInput(filters.since)} onChange={(e) => patch({ since: fromDateInput(e.target.value, 'start') })} aria-label='From date' title='From (inclusive)' />
				<input className='hist-filter-input' type='date' value={toDateInput(filters.until)} onChange={(e) => patch({ until: fromDateInput(e.target.value, 'end') })} aria-label='To date' title='To (inclusive — covers the whole day)' />
			</div>
		),
		username: (
			<select className='hist-filter-input' value={filters.userId ?? ''} onChange={(e) => patch({ userId: orUndefined(e.target.value) })} aria-label='Filter by user'>
				<option value=''>All</option>
				{users.map((u) => (
					<option key={u.id} value={u.id}>
						{u.username}
					</option>
				))}
			</select>
		),
		asset: (
			<input
				className='hist-filter-input'
				size={1}
				placeholder='All'
				value={assetText}
				onChange={(e) => setAssetText(e.target.value)}
				onBlur={commitAsset}
				onKeyDown={(e) => {
					if (e.key === 'Enter') commitAsset();
				}}
				aria-label='Filter by symbol'
			/>
		),
		timeframe: (
			<input
				className='hist-filter-input'
				size={1}
				placeholder='All'
				list='hist-tf-options'
				value={tfText}
				onChange={(e) => setTfText(e.target.value)}
				onBlur={commitTf}
				onKeyDown={(e) => {
					if (e.key === 'Enter') commitTf();
				}}
				aria-label='Filter by timeframe'
			/>
		),
		// Market rides along under Exchange: it is the same "which venue" question,
		// and it has no column of its own to sit under.
		exchange: (
			<div className='hist-filter-range'>
				<select className='hist-filter-input' value={filters.exchange ?? ''} onChange={(e) => patch({ exchange: orUndefined(e.target.value) })} aria-label='Filter by exchange'>
					<option value=''>All</option>
					{EXCHANGE_OPTIONS.map((x) => (
						<option key={x} value={x}>
							{x}
						</option>
					))}
				</select>
				<select className='hist-filter-input' value={filters.market ?? ''} onChange={(e) => patch({ market: orUndefined(e.target.value) })} aria-label='Filter by market' title='Matched against the job that ran the analysis — runs with no job row match no market.'>
					<option value=''>All markets</option>
					{MARKET_OPTIONS.map((m) => (
						<option key={m} value={m}>
							{m}
						</option>
					))}
				</select>
			</div>
		),
		// Multi-select, unlike its neighbours: comparing engines against each
		// other is the audit log's main use, and one-at-a-time made that two page
		// loads and a mental diff.
		engine: (
			<div className='hist-filter-range'>
				<MultiSelectFilter
					options={engineFilterOptions.map((e) => ({ value: e, label: engineVersionLabel(e) }))}
					selected={filters.engine ?? []}
					// [] would serialise to no filter anyway; dropping the key keeps it
					// out of activeFilterCount and the export's filter description too.
					onChange={(next) => patch({ engine: next.length ? next : undefined })}
					placeholder='All'
					ariaLabel='Filter by engine'
					title='Pick one or more engines to compare them side by side'
				/>
				{/* Forks sit under Engine because that is the question they answer:
				    which engine produced this row, and was it a real run or a replay.
				    Excluded by default — an experiment must never quietly move the
				    numbers the table is read for. */}
				<select
					className='hist-filter-input'
					value={filters.forks ?? 'exclude'}
					onChange={(e) => patch({ forks: e.target.value as ForkMode })}
					aria-label='Filter forked analyses'
					title="Forks replay a past run's stored input through another engine. Hidden by default so experiments don't move the production numbers."
				>
					<option value='exclude'>Real runs</option>
					<option value='include'>Runs + forks</option>
					<option value='only'>Forks only</option>
				</select>
			</div>
		),
		status: (
			<select className='hist-filter-input' value={filters.status ?? ''} onChange={(e) => patch({ status: orUndefined(e.target.value) })} aria-label='Filter by status'>
				<option value=''>All</option>
				<option value='success'>Success</option>
				<option value='error'>Error</option>
			</select>
		),
		trend: (
			<select className='hist-filter-input' value={filters.trend ?? ''} onChange={(e) => patch({ trend: orUndefined(e.target.value) })} aria-label='Filter by trend' title='Directional bias of the analysis. Prose-engine runs (v0/v1) carry no structured bias and match neither.'>
				<option value=''>All</option>
				<option value='bullish'>Bullish</option>
				<option value='bearish'>Bearish</option>
			</select>
		),
		side: (
			<select className='hist-filter-input' value={filters.side ?? ''} onChange={(e) => patch({ side: orUndefined(e.target.value) })} aria-label='Filter by side' title='Matches an analysis with at least one proposal that way — a run holding both sides matches either.'>
				<option value=''>All</option>
				<option value='long'>Long</option>
				<option value='short'>Short</option>
			</select>
		),
		// Filed under the first grade column and spanning all four (see the filter
		// row below): both controls scope the whole group — neither belongs to
		// Best any more than to Risk.
		[gradeKey(GRADE_COLUMNS[0])]: (
			<div className='hist-filter-range'>
				<select className='hist-filter-input' value={filters.verdict ?? ''} onChange={(e) => patch({ verdict: orUndefined(e.target.value) })} aria-label='Filter by verdict' title={`Verdicts are stored per rule set — this filters the ${ruleProse(pair)} sweep.`}>
					<option value=''>Any outcome</option>
					{VERDICT_FILTER_OPTIONS.map((v) => (
						<option key={v} value={v}>
							{verdictLabel(v as BacktestVerdict)}
						</option>
					))}
					<option value='SKIPPED_NO_TRADES'>No trades</option>
				</select>
				<select className='hist-filter-input' value={filters.grade ?? ''} onChange={(e) => patch({ grade: (orUndefined(e.target.value) as BacktestGrade) ?? undefined })} aria-label='Filter by trade grade' title={`The grade the engine gave the decisive proposal. Stored per rule set, so this filters the ${ruleProse(pair)} sweep.`}>
					<option value=''>Any grade</option>
					{GRADE_ORDER.map((g) => (
						<option key={g} value={g}>
							{gradeLabel(g)}
						</option>
					))}
				</select>
			</div>
		),
		// total_tokens has no server-side filter, so its cell stays empty.
	};

	// Excel export of every analysis matching the current filters (server-built,
	// so it covers the whole filtered set rather than just the visible page).
	const [exporting, setExporting] = useState(false);
	const [exportError, setExportError] = useState<string | null>(null);

	const doExport = async () => {
		if (exporting) return; // the request is slow — guard against double-clicks
		setExporting(true);
		setExportError(null);
		try {
			const { blob, filename } = await exportAnalyses(filters, pair);
			downloadBlob(filename ?? `nobrainer-analyses-${Date.now()}.xlsx`, blob);
		} catch (e) {
			setExportError(e instanceof Error ? e.message : 'Export failed');
		} finally {
			setExporting(false);
		}
	};

	return (
		<div className='hist-view'>
			<div className='hist-toolbar'>
				<div className='hist-toolbar-left'>
					<span className='hist-title'>Analysis history</span>
					<span className='hist-count'>{status === 'success' && rows.length > 0 ? `${offset + 1}–${offset + rows.length} of ${total}` : status === 'success' ? `0 of ${total}` : ''}</span>
				</div>
				<div className='hist-filters'>
					{/* Phones hide the table header (rows become cards), so the per-column
					    filters have nowhere to live — surface them in a panel instead. */}
					<button className={`hist-refresh hist-filters-toggle ${mobileFiltersOpen ? 'active' : ''}`} onClick={() => setMobileFiltersOpen((v) => !v)} aria-expanded={mobileFiltersOpen}>
						Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
					</button>
					{/* Mobile-only sort control: on phones the card layout hides the
              tap-to-sort table headers, so expose column + direction here. */}
					<div className='hist-sort-mobile'>
						<select className='hist-input' value={sortKey ?? ''} onChange={(e) => sortBy(e.target.value)} aria-label='Sort by'>
							<option value='' disabled>
								Sort by…
							</option>
							{SORTABLE_COLUMNS.map((c) => (
								<option key={c.key} value={c.sortKey}>
									{c.label}
								</option>
							))}
						</select>
						<button className='hist-refresh' onClick={() => sortKey && sortBy(sortKey)} disabled={!sortKey} aria-label='Toggle sort direction' title='Toggle sort direction'>
							{sortDir === 'asc' ? '▲' : '▼'}
						</button>
					</div>
					{activeFilterCount > 0 && (
						<button className='hist-refresh' onClick={clearFilters} title='Clear all filters'>
							Clear ({activeFilterCount})
						</button>
					)}
					<button className='hist-refresh hist-export' onClick={doExport} disabled={exporting} title={exporting ? 'Building the workbook…' : `Export all ${total} analyses matching the current filters to Excel (data source, Enforcer and Claude in/out)`}>
						{exporting ? 'Exporting…' : 'Export'}
					</button>
					<button className='hist-refresh' onClick={refresh} title='Reload'>
						↻
					</button>
				</div>
			</div>

			{/* Shared by the symbol/TF inputs wherever they are rendered. */}
			<datalist id='hist-tf-options'>
				{TIMEFRAME_SUGGESTIONS.map((tf) => (
					<option key={tf} value={tf} />
				))}
			</datalist>

			{mobileFiltersOpen && (
				<div className='hist-filters-panel'>
					{COLUMNS.filter((c) => filterControls[c.key]).map((c) => (
						<label key={c.key} className='hist-filters-panel-field'>
							<span className='hist-filters-panel-label'>{c.key === 'exchange' ? 'Exchange / market' : c.label}</span>
							{filterControls[c.key]}
						</label>
					))}
				</div>
			)}

			<BacktestSummaryBar bt={bt} pair={pair} setPair={setPair} onOpenSettings={() => setSettingsOpen(true)} filtered={activeFilterCount > 0} filters={filters} onForksCreated={refresh} currentReplayVersion={settings.settings?.replay_version ?? 1} />

			{exportError && <div className='hist-error'>Export failed: {exportError}</div>}

			{error && <div className='hist-error'>{error}</div>}

			{!error && status === 'loading' && rows.length === 0 && <div className='hist-empty'>Loading…</div>}

			{!error && status === 'success' && rows.length === 0 && <div className='hist-empty'>{activeFilterCount > 0 ? 'No analyses match these filters.' : 'No analyses recorded yet.'}</div>}

			{rows.length > 0 && (
				<div className='hist-table-wrap'>
					<table className='hist-table'>
						<thead>
							<tr>
								{COLUMNS.map((c) =>
									c.sortKey ? (
										<th key={c.key} className={`hist-th hist-col-${c.key} ${sortKey === c.sortKey ? 'active' : ''}`} onClick={() => sortBy(c.sortKey!)}>
											{c.label}
											<span className='hist-caret'>{sortKey === c.sortKey ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>
										</th>
									) : (
										// Derived from the run's output / backtest, not a stored column —
										// the server has nothing to order by, so no sort affordance.
										// Grade heads carry their own tone, matching the summary bar.
										<th key={c.key} className={`hist-th hist-th-static hist-col-${c.grade ? 'grade' : c.key} ${c.grade ? `hist-th-grade bt-tone-${gradeTone(c.grade)}` : ''}`} title={c.grade ? `What the engine's "${gradeLabel(c.grade)}" call did on each analysis` : undefined}>
											{c.label}
										</th>
									),
								)}
								{/* Per-row zip download. Rendered outside COLUMNS on purpose: that
								    array also drives sorting and the mobile filters panel, and an
								    action cell is neither a sort key nor a filter. */}
								<th className='hist-th hist-th-static hist-col-zip' title='Download a run as one .zip: every pipeline step, the forward candles and the replay verdicts'>
									<span aria-hidden='true'>⭳</span>
								</th>
							</tr>
							{/* Filter row. Separate <tr> from the labels above so a click on a
							    control never reaches the column's sort handler. */}
							<tr className='hist-filter-row'>
								{/* Side has no cell — it is derived from the run's own output, so
								    there is no server-side filter to drive. The verdict/grade
								    controls sit once across the whole grade group rather than
								    under any one of its columns. */}
								{COLUMNS.map((c) => {
									const firstGrade = c.grade === GRADE_COLUMNS[0];
									if (c.grade && !firstGrade) return null; // covered by the span
									return (
										<th key={c.key} colSpan={firstGrade ? GRADE_COLUMNS.length : undefined} className={`hist-filter-th hist-col-${c.grade ? 'grade-group' : c.key}`}>
											{filterControls[c.key] ?? null}
										</th>
									);
								})}
								<th className='hist-filter-th hist-col-zip' />
							</tr>
						</thead>
						<tbody>
							{rows.map((r) => {
								const rb = bt.byId.get(r.id);
								return (
									<tr key={r.id} className='hist-row' onClick={() => setSelected(r)}>
										{/* as_of over created_at: for a fork the row was written today but
										    describes its parent's moment, which is what the backtest scored
										    it against. Showing created_at would put a replay of last week
										    under today's date. */}
										<td data-label='#' className='hist-no'>{analysisNo(r.seq)}</td>
										<td data-label='Date / time'>{fmtDateTime(r.as_of ?? r.created_at)}</td>
										<td data-label='User' title={r.username ?? undefined}>
											{r.username ?? <span className='hist-muted'>—</span>}
										</td>
										<td data-label='Symbol'>{r.asset}</td>
										<td data-label='Trend'>
											<TrendBadge trend={r.trend} />
										</td>
										<td data-label='Side'>
											<SideBadge longs={r.long_count} shorts={r.short_count} />
										</td>
										<td data-label='TF'>{r.timeframe}</td>
										<td data-label='Exchange'>{r.exchange}</td>
										{/* Version-first: the cell truncates, and the version is the half
										    that distinguishes one run from another. Full name on hover. */}
										<td data-label='Engine' title={engineLabel(r.engine)}>
											{engineVersionLabel(r.engine)}
											{r.is_fork && (
												<span className='hist-fork-tag' title="Replay of another run's stored input">
													fork
												</span>
											)}
										</td>
										{/* <td data-label='Status'>
											<span className={`hist-status hist-status-${r.status}`}>{r.status}</span>
										</td> */}
										{/* <td data-label='Tokens'>{r.total_tokens || <span className='hist-muted'>—</span>}</td> */}
										<GradeCells row={r} rollup={rb} loading={bt.loadingIds} />
										<ZipCell row={r} />
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}

			{!error && total > 0 && (
				<div className='hist-pager'>
					<button className='hist-refresh' onClick={goPrev} disabled={!canPrev || status === 'loading'}>
						← Prev
					</button>
					<span className='hist-pager-info'>
						{rows.length > 0 ? `${offset + 1}–${offset + rows.length}` : '0'} of {total}
					</span>
					<button className='hist-refresh' onClick={goNext} disabled={!canNext || status === 'loading'}>
						Next →
					</button>
				</div>
			)}

			{selected && <HistoryDetailModal row={selected} windowPair={pair} onClose={() => setSelected(null)} />}

			<BacktestSettingsModal open={settingsOpen} settings={settings.settings} saving={settings.saving} error={settings.error} onSave={saveSettingsAndView} onClose={() => setSettingsOpen(false)} />
		</div>
	);
}

/**
 * The analysis's overall directional read.
 *
 * A pill rather than SideBadge's arrow text, because the two sit next to each
 * other and answer different questions — the trend is the thesis, the side is
 * what was actually proposed, and a bullish read can still carry a short.
 */
/**
 * The four grade cells of one History row: what our Best / Decent / Risk / High
 * Risk call did on this analysis.
 *
 * Some states are properties of the whole row rather than of any one grade — a
 * mock run, one never swept, one the engine declined to trade. Those span the
 * group instead of repeating a dash four times, which also keeps them
 * distinguishable from "we made no Risk call here" (a plain dash in one cell).
 */
function GradeCells({ row, rollup, loading }: { row: AnalysisAuditSummary; rollup: BacktestRollup | undefined; loading: boolean }) {
	const span = (content: React.ReactNode, title?: string) => (
		<td className='hist-grade-span' colSpan={GRADE_COLUMNS.length} data-label='Outcome' title={title}>
			<span className='hist-muted'>{content}</span>
		</td>
	);

	if (row.mock) return span('mock', 'Mock analysis — synthetic data, not backtestable');
	if (!rollup) return span(loading ? '…' : 'not backtested', loading ? undefined : 'No stored result for this rule set yet — use "Run missing"');
	if (rollup.status === 'SKIPPED_NO_TRADES') return span('no trades', 'The analysis proposed no trades at all');

	const byGrade = outcomesByGrade(rollup.proposals);
	// Nothing the engine proposed carries one of the four graded labels — a Wait
	// call, or an engine that labels differently. Showing four dashes would read
	// as "not backtested", so the row says what actually happened instead.
	if (!GRADE_COLUMNS.some((g) => byGrade.has(g))) {
		const rest = [...byGrade.values()].flat();
		return span(rest.map((o) => `${verdictLabel(o.verdict)}${o.count > 1 ? ` ×${o.count}` : ''}`).join(' · ') || '—', rest.map((o) => `${gradeLabel(o.grade)} → ${verdictLabel(o.verdict)}`).join(', '));
	}

	return (
		<>
			{GRADE_COLUMNS.map((g) => {
				const outcomes = byGrade.get(g);
				return (
					// Flagged when empty so the card layout can drop it — four "—" rows
					// per card is noise, while in the table the gap is the information.
					<td key={g} className={`hist-col-grade ${outcomes ? '' : 'hist-grade-empty'}`} data-label={gradeShortLabel(g)}>
						{outcomes ? (
							<div className='bt-outcomes'>
								{outcomes.map((o) => (
									// No grade suffix here — the column already is the grade.
									<span key={o.verdict} className={`bt-vbadge bt-tone-${verdictTone(o.verdict)}`} title={`${o.count} ${gradeLabel(g)} ${o.count === 1 ? 'proposal' : 'proposals'} → ${verdictLabel(o.verdict)}`}>
										{verdictLabel(o.verdict)}
										{o.count > 1 ? ` ×${o.count}` : ''}
									</span>
								))}
							</div>
						) : (
							<span className='hist-muted' title={`No ${gradeLabel(g)} proposal on this analysis`}>
								—
							</span>
						)}
					</td>
				);
			})}
		</>
	);
}

/**
 * One-click zip of a row straight from the table: the same bundle the detail
 * modal's "↓ All (.zip)" builds, without opening the modal first.
 *
 * The list row carries summary fields only, so the full record (trace, output)
 * is fetched on click; `buildTraceBundle` then fetches the forward candles and
 * the replay verdicts itself. Each cell owns its busy/error state, as the
 * admin UsersView rows do — there is no global per-row state or toast here.
 */
function ZipCell({ row }: { row: AnalysisAuditSummary }) {
	const [busy, setBusy] = useState(false);
	const [err, setErr] = useState<string | null>(null);

	const download = async (e: React.MouseEvent) => {
		// The whole <tr> opens the detail modal; a download must not.
		e.stopPropagation();
		if (busy) return;
		setBusy(true);
		setErr(null);
		try {
			const detail = await getAnalysis(row.id);
			const { base, files } = await buildTraceBundle(detail);
			await downloadZip(`${base}.zip`, files);
		} catch (ex) {
			setErr(ex instanceof ApiError ? `${ex.message} (${ex.status})` : ex instanceof Error ? ex.message : 'Download failed');
		} finally {
			setBusy(false);
		}
	};

	return (
		<td className='hist-col-zip' data-label='Download' onClick={(e) => e.stopPropagation()}>
			<button
				type='button'
				className={`hist-download hist-download-sm ${err ? 'hist-download-err' : ''}`}
				onClick={download}
				disabled={busy}
				aria-label={`Download ${analysisNo(row.seq)} as .zip`}
				title={err ? `Download failed: ${err} — click to retry` : 'Download every pipeline step, the forward candles and the replay verdicts as one .zip'}
			>
				{busy ? '…' : err ? '⚠' : '⭳'}
			</button>
		</td>
	);
}

function TrendBadge({ trend }: { trend: 'BULLISH' | 'BEARISH' | null }) {
	if (!trend) {
		return (
			<span className='hist-muted' title='No structured bias — the v0/v1 prose engines do not emit one'>
				—
			</span>
		);
	}
	const bull = trend === 'BULLISH';
	return <span className={bull ? 'hist-trend-bull' : 'hist-trend-bear'}>{bull ? 'Bull' : 'Bear'}</span>;
}

/**
 * The direction of an analysis's trade proposals.
 *
 * An analysis can hold several, so this is not always one label: a run that
 * proposes both ways shows both counts rather than picking a winner, which
 * would misrepresent what the analyst actually said.
 */
function SideBadge({ longs, shorts }: { longs: number; shorts: number }) {
	if (!longs && !shorts) {
		return (
			<span className='hist-muted' title='No trade proposals with a readable direction'>
				—
			</span>
		);
	}
	if (longs && shorts) {
		return (
			<span className='hist-side-mixed' title={`${longs} long · ${shorts} short`}>
				<span className='hist-side-long'>↑{longs}</span>
				<span className='hist-side-short'>↓{shorts}</span>
			</span>
		);
	}
	const isLong = longs > 0;
	const n = isLong ? longs : shorts;
	return (
		<span className={isLong ? 'hist-side-long' : 'hist-side-short'} title={`${n} ${isLong ? 'long' : 'short'} proposal${n > 1 ? 's' : ''}`}>
			{isLong ? '↑' : '↓'}
			{n > 1 && <span className='hist-side-count'> ×{n}</span>}
		</span>
	);
}

/**
 * Label for a stored sweep's rule set, e.g. "6 · no stop" or "6 · close 20".
 *
 * Spelled out rather than shown as "6 / ∞": the infinity read as an *entry*
 * window of unlimited length, when it actually meant the opposite half of the
 * pair — no time stop, so a filled trade runs until it hits a target or a stop.
 */
/** The rule set spelled out for a tooltip or a confirm dialog. */
function ruleProse(p: BacktestRuleSet): string {
	const stop = p.stopMult === 1 ? 'stop as proposed' : `stop ${p.stopMult}× wider`;
	return `entry ${p.window} / close ${p.closeWindow || 'none'} / ${stop}`;
}

function pairLabel(p: BacktestRuleSet, current: number): string {
	const windows = p.closeWindow ? `${p.window} · close ${p.closeWindow}` : `${p.window} · no stop`;
	// The baseline is spelled out rather than shown as a bare "1x": it is the
	// one option whose numbers are the analyses' own, and mistaking a widened
	// sweep for it would make every win rate on screen read as the real one.
	const sl = p.stopMult === 1 ? 'SL as proposed' : `SL ${p.stopMult}×`;
	// Same reasoning for the engine: an older sweep's win rate is not comparable
	// to the current one's, so it must never look like an unlabelled default.
	const engine = p.replayVersion === current ? '' : ` · engine v${p.replayVersion}`;
	return `${windows} · ${sl}${engine}`;
}

function BacktestSummaryBar({
	bt,
	pair,
	setPair,
	onOpenSettings,
	filtered,
	filters,
	onForksCreated,
	currentReplayVersion,
}: {
	bt: ReturnType<typeof useBacktestList>;
	pair: BacktestRuleSet;
	setPair: (p: BacktestRuleSet) => void;
	onOpenSettings: () => void;
	/** The replay engine this build writes, so the picker can mark any stored set
	 *  from an older one instead of letting it pass as the current numbers. */
	currentReplayVersion: number;
	/** Whether any History filter is active — the stats follow them, so the bar
	 *  has to say when it is describing a subset rather than everything. */
	filtered: boolean;
	/** The active filters, so a batch fork covers exactly the visible set. */
	filters: AnalysisHistoryFilters;
	/** Reload the table once a sweep has produced rows. */
	onForksCreated: () => void;
}) {
	const a = bt.aggregate;
	// The engine's own top grade. Undefined when nothing was graded "Best" in the
	// filtered set, or when the set was swept before grades were tracked.
	const best = a?.grades_known ? a.by_grade?.best : undefined;
	if (bt.error) {
		return <div className='bt-error'>Backtest unavailable: {bt.error}</div>;
	}

	// Offer every stored pair plus the active one, so a pair that has not been
	// swept yet is still selectable (and visibly empty) rather than missing.
	const toRules = (c: BacktestCombo): BacktestRuleSet => toRuleSet(c);
	const options: BacktestRuleSet[] = [...(bt.combos.some((c) => sameRules(toRules(c), pair)) ? [] : [pair]), ...bt.combos.map(toRules)];
	// The engine this build writes — anything else in the picker is a baseline
	// from an older replay and gets labelled as such.
	const currentEngine = currentReplayVersion;
	const staleEngine = pair.replayVersion !== currentEngine;
	const timeStopOn = pair.closeWindow > 0;

	return (
		<div className='bt-summary'>
			{/* The lead tile is the Best-graded win rate, not the blended one.
			    An analysis emits up to four proposals and nothing stops it pairing
			    a LONG with a SHORT on the same market — those are structurally
			    hedged, so one of the pair is close to guaranteed to stop out. Add
			    the Risk and High Risk grades to the same bucket and the blended
			    number is dragged toward 50% by construction. "Do our best calls
			    win?" is the question this bar exists to answer. */}
			<div className='bt-summary-stat'>
				<span className={`bt-summary-val ${best && best.win_rate != null ? (best.win_rate >= 0.5 ? 'good' : 'bad') : ''}`} title={'Targets hit vs stops hit, across proposals the engine graded "Best" only. Every figure in this bar covers the analyses matching the filters above, not the whole database.'}>
					{a && !a.grades_known ? '—' : best ? fmtPct(best.win_rate) : '—'}
				</span>
				<span className='bt-summary-label'>
					Win rate (Best)
					{/* Which set these numbers describe. Without it a filtered win rate
					    is indistinguishable from the global one — same tile, same
					    styling, very different claim. */}
					{a && <span className={`bt-summary-sub ${filtered ? 'bt-summary-scoped' : ''}`}> · {filtered ? `${a.count_analyses.toLocaleString()} filtered` : `${a.count_analyses.toLocaleString()} analyses`}</span>}
				</span>
			</div>
			<div className='bt-summary-stat'>
				<span className='bt-summary-val bt-summary-muted' title='Targets hit vs stops hit across EVERY proposal, of every grade and both directions. One analysis can propose a long and a short on the same market — those cannot both win, so this number is pulled toward 50% by construction. Read it as a floor, not as the engine&rsquo;s hit rate.'>
					{a ? fmtPct(a.win_rate) : '—'}
				</span>
				<span className='bt-summary-label'>All proposals</span>
			</div>
			<div className='bt-summary-stat'>
				<span className='bt-summary-val'>{a ? fmtPct(a.fill_rate) : '—'}</span>
				<span className='bt-summary-label'>Fill rate</span>
			</div>
			<div className='bt-summary-stat'>
				<span className='bt-summary-val'>{a ? fmtPct(a.no_entry_rate) : '—'}</span>
				<span className='bt-summary-label'>No-entry rate</span>
			</div>
			{/* Direction split. Shows "—" rather than 0% when the stored set predates
			    the direction tallies — unknown is not the same as none. */}
			<div className='bt-summary-stat'>
				{a && !a.direction_known ? (
					<span className='bt-summary-val bt-summary-unknown' title='Stored before long/short was tracked — re-run to compute'>
						—
					</span>
				) : (
					<span className='bt-summary-val'>
						<span className='hist-side-long'>{a ? fmtPct(a.long_rate) : '—'}</span>
						<span className='bt-summary-split'>/</span>
						<span className='hist-side-short'>{a ? fmtPct(a.short_rate) : '—'}</span>
					</span>
				)}
				<span className='bt-summary-label'>
					Long / short
					{a && a.direction_known && (a.count_long > 0 || a.count_short > 0) && (
						<span className='bt-summary-sub'>
							{' '}
							· {a.count_long}/{a.count_short}
						</span>
					)}
				</span>
			</div>
			{/* Only meaningful with a time stop, and always 0 without one — so it
          earns its slot in the bar only when the deadline is enabled. */}
			{timeStopOn && (
				<div className='bt-summary-stat'>
					<span className='bt-summary-val'>{a ? fmtPct(a.timeout_rate) : '—'}</span>
					<span className='bt-summary-label'>Timeout rate</span>
				</div>
			)}
			<div className='bt-summary-stat'>
				<span className={`bt-summary-val ${a && a.avg_realized_R != null ? (a.avg_realized_R >= 0 ? 'good' : 'bad') : ''}`}>{a ? fmtR(a.avg_realized_R) : '—'}</span>
				<span className='bt-summary-label'>Avg realized R</span>
			</div>
			<div className='bt-summary-stat'>
				<span className='bt-summary-val'>{a ? fmtR(a.expectancy_R) : '—'}</span>
				<span className='bt-summary-label'>Expectancy</span>
			</div>
			<div className='bt-summary-stat'>
				<span className='bt-summary-val'>{a ? `${a.counts.tp ?? 0}/${a.counts.sl ?? 0}${timeStopOn ? `/${a.counts.timeout ?? 0}` : ''}` : '—'}</span>
				<span className='bt-summary-label'>{timeStopOn ? 'TP / SL / TO' : 'TP / SL'}</span>
			</div>

			<div className='bt-summary-spacer' />

			<div className='bt-run'>
				{/* A sweep always writes the engine this build runs, so it cannot
				    refresh an older baseline — pressing Run while viewing one would
				    silently populate a different rule set and look like nothing
				    happened. The baseline is read-only by nature; say so.

				    Both versions here are guaranteed to be real numbers because
				    every rule set is built by toRuleSet. That matters: when the
				    field could be undefined this branch was entered against *any*
				    backend that had not shipped the engine version yet, which hid
				    the run buttons entirely and left no way to sweep at all. */}
				{staleEngine ? (
					<span className='bt-run-progress'>Engine v{pair.replayVersion} is a stored baseline — switch to v{currentEngine} to sweep.</span>
				) : bt.run?.status === 'running' ? (
					<span className='bt-run-progress'>
						{bt.run.scope === 'all' ? 'Rebuilding all history' : 'Filling in missing verdicts'}… {bt.run.processed}/{bt.run.total}
					</span>
				) : (
					<>
						{/* The default action. Adds and refreshes only — nothing is
						    deleted — so it is safe to press whenever new analyses have
						    accumulated or an OPEN trade has had time to resolve. */}
						<button
							className='bt-run-btn'
							onClick={() => bt.startRun('missing')}
							disabled={bt.missingCount === 0}
							title={bt.missingCount === 0 ? `Every analysis already has a settled verdict under ${ruleProse(pair)}.` : `Backtest the ${bt.missingCount} analyses with no settled verdict yet — never backtested, or still open/pending. Nothing stored is deleted.`}
						>
							{bt.missingCount > 0 ? `Run missing (${bt.missingCount})` : 'Nothing missing'}
						</button>
						{/* Destructive: it clears this rule set before rebuilding, so an
						    interrupted run leaves a partial set that still reads as
						    authoritative. Hence the confirm and the quieter styling.
						    The confirm names the stop multiplier too — without it,
						    wiping the 2× sweep and wiping the baseline look identical. */}
						<button
							className='bt-run-btn bt-run-btn-danger'
							onClick={() => {
								if (bt.total > 0 && !window.confirm(`Delete all ${bt.total} stored results for ${ruleProse(pair)} and recompute them from scratch?\n\nOther rule sets are untouched. "Run missing" is enough to pick up new analyses.`)) return;
								bt.startRun('all');
							}}
							title={`Drop every stored result for ${ruleProse(pair)} and recompute it (other rule sets are untouched)`}
						>
							{bt.total > 0 ? 'Re-run all' : 'Run over all history'}
						</button>
						{bt.run?.status === 'error' && <span className='bt-run-err'>{bt.run.error}</span>}
						{/* A finished run can still be incomplete — surface it, or the
                aggregate silently under-reports. */}
						{bt.run?.status === 'done' && !!bt.run.skipped && <span className='bt-run-err'>{bt.run.skipped} skipped</span>}
						{bt.lastComputedAt && <span className='bt-run-last'>updated {fmtDateTime(bt.lastComputedAt)}</span>}
						{/* Sits with the sweep controls because it is the same kind of
						    action — a long, unattended batch over the filtered set — and
						    because its output is what the next backtest run scores. */}
						<ForkSweepBar filters={filters} onFinished={onForksCreated} />
					</>
				)}
			</div>

			<div className='bt-window'>
				<span className='bt-window-label' title={`Which stored backtest you are looking at. Each is one run over all history under one rule set — entry window (candles a limit has to fill in), close window (time stop after fill; "no stop" = the trade runs to TP or SL), and stop multiplier ("SL as proposed" = the stop each analysis proposed; "SL 2×" replays it twice as far away with the same targets). Switching just re-reads stored results; "Re-run all" recomputes the selected one.`}>
					Viewing
				</span>
				<select
					className='bt-window-select'
					value={ruleKey(pair)}
					onChange={(e) => {
						// Look the option up by key rather than parsing it back: the key
						// is a display detail, and a parser here silently breaks the
						// moment a field stops being a bare number.
						const picked = options.find((p) => ruleKey(p) === e.target.value);
						if (picked) setPair(picked);
					}}
					aria-label='Backtest rule set'
				>
					{options.map((p) => {
						const combo = bt.combos.find((c) => sameRules(toRules(c), p));
						return (
							<option key={ruleKey(p)} value={ruleKey(p)}>
								{pairLabel(p, currentEngine)}
								{combo ? ` · ${combo.count}` : ' · not run'}
							</option>
						);
					})}
				</select>
				<button className='bt-window-cog' onClick={onOpenSettings} title='Backtest settings' aria-label='Backtest settings'>
					⚙
				</button>
			</div>

			<GradeBreakdown aggregate={a} />
		</div>
	);
}

/**
 * How each grade of trade the engine emits actually performed.
 *
 * The question the backtest exists to answer: the engine calls some setups Best
 * and others Risk, and until now nothing checked whether that ranking survives
 * contact with the candles. One row per grade, in the engine's own order, with
 * the count first — a 100% win rate over two trades is not the same finding as
 * 58% over four hundred.
 */
function GradeBreakdown({ aggregate }: { aggregate: BacktestAggregate | null }) {
	if (!aggregate) return null;

	// NULL label_counts means "swept before grades were tracked" — reporting an
	// empty breakdown would read as "no Best trades exist", a different and wrong
	// claim. Same distinction the direction split makes.
	if (!aggregate.grades_known) {
		return (
			<div className='bt-grades'>
				<span className='bt-grades-label'>By grade</span>
				<span className='bt-grades-empty' title='These results were computed before trade grades were tracked'>
					not computed for this sweep — re-run to populate
				</span>
			</div>
		);
	}

	// The four grades the engine assigns to tradeable setups are always shown,
	// even at zero: an empty Risk column is itself a finding ("we never propose
	// those"), and a header that keeps its shape is what makes the numbers
	// scannable week to week. Wait / Other are not trade grades, so they earn a
	// column only when they actually hold something.
	const columns = [...GRADE_COLUMNS, ...GRADE_EXTRA_COLUMNS.filter((g) => (aggregate.by_grade[g]?.n ?? 0) > 0)];
	if (!columns.some((g) => (aggregate.by_grade[g]?.n ?? 0) > 0)) return null;

	const cell = (s: BacktestGradeStats | undefined, key: GradeRow['key']) => s?.[key] ?? 0;

	return (
		<div className='bt-grades'>
			<table className='bt-grade-table'>
				<thead>
					<tr>
						<th className='bt-grades-label' scope='col' title="The engine's own risk label for each proposal, scored against what the candles actually did.">
							By grade
						</th>
						{columns.map((g) => (
							<th key={g} scope='col' className={`bt-grade-col bt-tone-${gradeTone(g)}`}>
								{gradeShortLabel(g)}
								{/* Total proposals under this grade, so the rows below are
								    read against a base — 3 TP out of 4 is not 3 out of 40. */}
								<span className='bt-grade-n'>{aggregate.by_grade[g]?.n ?? 0}</span>
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{GRADE_ROWS.filter(
						// Always-on rows keep the table's shape; the rest would just be a
						// line of zeros with the time stop off or nothing left running.
						(row) => row.always || columns.some((g) => cell(aggregate.by_grade[g], row.key) > 0),
					).map((row) => (
						<tr key={row.key}>
							<th scope='row' className='bt-grade-row-label' title={row.title}>
								{row.label}
							</th>
							{columns.map((g) => {
								const n = cell(aggregate.by_grade[g], row.key);
								// Zeros are muted so the eye lands on what actually happened.
								return (
									<td key={g} className={`bt-grade-cell ${n ? (row.tone ?? '') : 'zero'}`}>
										{n}
									</td>
								);
							})}
						</tr>
					))}
					<tr className='bt-grade-wr-row'>
						<th scope='row' className='bt-grade-row-label' title='Targets hit vs stops hit, for proposals carrying this grade'>
							WR
						</th>
						{columns.map((g) => {
							const s = aggregate.by_grade[g];
							const decided = (s?.tp ?? 0) + (s?.sl ?? 0);
							return (
								<td
									key={g}
									// A grade with nothing decided yet (all Wait, or all still
									// running) has no win rate — "—" beats a misleading 0%.
									className={`bt-grade-cell bt-grade-wr ${decided ? (s!.win_rate != null && s!.win_rate >= 0.5 ? 'good' : 'bad') : 'zero'}`}
									title={decided ? `${s!.tp} TP / ${s!.sl} SL · avg ${fmtR(s!.avg_realized_R)} · expectancy ${fmtR(s!.expectancy_R)}` : 'Nothing resolved to a target or a stop yet'}
								>
									{decided ? fmtPct(s!.win_rate) : '—'}
								</td>
							);
						})}
					</tr>
				</tbody>
			</table>
		</div>
	);
}
